import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle2, Clock, AlertTriangle, Download, ArrowRight } from 'lucide-react';
import Logo from '../../components/Logo';
import axios from 'axios';

import { API_URL } from '../../utils/api';

const SignatureCallback = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, pending, error
  const [data, setData] = useState(null);

  useEffect(() => {
    const checkSignatureStatus = async () => {
      try {
        // Check signature status
        const response = await axios.get(`${API_URL}/portal/third-party/${token}/signature-status`);
        
        if (response.data.signed) {
          setStatus('success');
        } else if (response.data.signature_status === 'ongoing' || response.data.signature_status === 'STARTED') {
          setStatus('pending');
        } else {
          setStatus('pending');
        }
        
        // Get claim data
        const claimResponse = await axios.get(`${API_URL}/portal/third-party/${token}`);
        setData(claimResponse.data);
      } catch (err) {
        console.error('Error checking status:', err);
        setStatus('error');
      }
    };

    checkSignatureStatus();
    
    // Poll for status updates if pending
    const interval = setInterval(async () => {
      if (status === 'pending') {
        try {
          const response = await axios.get(`${API_URL}/portal/third-party/${token}/signature-status`);
          if (response.data.signed) {
            setStatus('success');
            clearInterval(interval);
          }
        } catch (err) {
          console.error('Polling error:', err);
        }
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [token, status]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="signature-callback">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-4 md:px-6">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Logo size="default" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 md:p-8">
        {status === 'checking' && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
              <h2 className="text-xl font-bold text-blue-900 mb-2">Vérification en cours...</h2>
              <p className="text-blue-700">
                Nous vérifions le statut de votre signature.
              </p>
            </CardContent>
          </Card>
        )}

        {status === 'success' && (
          <div className="space-y-6">
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="py-12 text-center">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-emerald-900 mb-3">
                  Signature confirmée !
                </h2>
                <p className="text-emerald-700 text-lg">
                  Votre quittance transactionnelle a été signée avec succès.
                </p>
                {data && (
                  <p className="text-emerald-600 mt-2">
                    Dossier <strong>{data.claim_reference}</strong>
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6 space-y-4">
                <h3 className="font-semibold text-lg">Prochaines étapes</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-emerald-800">Quittance signée électroniquement</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                    <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-blue-800">Le paiement au garage sera effectué sous 48h</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Clock className="w-5 h-5 text-slate-600 flex-shrink-0" />
                    <span className="text-slate-700">Vous pouvez prendre RDV avec votre garage</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {data && (
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Download className="w-5 h-5 text-blue-600" />
                    Télécharger votre quittance
                  </h3>
                  <p className="text-sm text-blue-700 mb-4">
                    Conservez une copie de la quittance signée pour vos archives.
                  </p>
                  <Button 
                    onClick={() => {
                      window.open(`${API_URL}/portal/third-party/${token}/settlement-pdf`, '_blank');
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger la quittance (PDF)
                  </Button>
                </CardContent>
              </Card>
            )}

            <div className="text-center">
              <Button
                variant="outline"
                onClick={() => window.location.href = `/portal/third-party/${token}`}
              >
                Retour au portail
              </Button>
            </div>
          </div>
        )}

        {status === 'pending' && (
          <div className="space-y-6">
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="py-12 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-amber-600" />
                </div>
                <h2 className="text-xl font-bold text-amber-900 mb-2">
                  Signature en cours de traitement
                </h2>
                <p className="text-amber-700">
                  Votre signature est en cours de validation. 
                  Cette page se mettra à jour automatiquement.
                </p>
              </CardContent>
            </Card>

            <div className="text-center">
              <Button
                variant="outline"
                onClick={() => window.location.href = `/portal/third-party/${token}`}
              >
                Retour au portail
              </Button>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-6">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="py-12 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-red-900 mb-2">
                  Une erreur est survenue
                </h2>
                <p className="text-red-700">
                  Impossible de vérifier le statut de votre signature.
                  Veuillez retourner au portail pour vérifier l'état de votre dossier.
                </p>
              </CardContent>
            </Card>

            <div className="text-center">
              <Button
                onClick={() => window.location.href = `/portal/third-party/${token}`}
                className="bg-slate-900 hover:bg-slate-800"
              >
                Retour au portail
              </Button>
            </div>
          </div>
        )}
      </main>

      <footer className="py-6 text-center text-sm text-slate-500 border-t border-slate-200 mt-8">
        <p>© 2026 C2 ASSURANCES</p>
        <p className="mt-1 text-xs">Signature électronique certifiée par EDI Signature (Docapost)</p>
      </footer>
    </div>
  );
};

export default SignatureCallback;
